import { useEffect, useRef, useState } from 'react';
import CubMascot from './CubMascot.jsx';
import { canSpeak, useVoice, speak, hush } from '../lib/speech.js';

/**
 * Cub, explaining whatever is on screen.
 *
 * A practical task can be worked through in silence, and plenty of
 * children will. This is for the ones who get further with someone
 * talking them through it - and for anybody who finds a wall of written
 * instructions harder work than being told.
 *
 * Two rules it follows:
 *
 *   It is quiet until asked. Nothing speaks on arrival, partly because
 *   a browser will refuse audio nobody requested, and mostly because a
 *   classroom of machines starting to talk at once is a disaster.
 *
 *   It never speaks over itself. Moving to the next thing cuts the last
 *   one off mid-word, which is what you want: the learner has moved on,
 *   and the voice should be where they are, not where they were.
 *
 * The words are always on screen too. The voice is an alternative to
 * reading, never the only copy.
 */
export default function Tutor({ say, avatar = 'cub-teal', className = '' }) {
  const [on, setOn] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const voice = useVoice();
  const available = canSpeak();

  // What is currently being said, so turning the tutor on says the
  // thing on screen now rather than waiting for the next change.
  const latest = useRef(say);
  latest.current = say;

  useEffect(() => {
    if (!on) return undefined;
    speak(say, voice, {
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false),
    });
    return () => { hush(); setSpeaking(false); };
  }, [on, say, voice]);

  // Never leave a voice talking to a page nobody is on.
  useEffect(() => () => hush(), []);

  if (!available) return null;

  const toggle = () => {
    setOn((was) => {
      if (was) { hush(); setSpeaking(false); return false; }
      return true;
    });
  };

  return (
    <div className={`tutor ${on ? 'is-on' : ''} ${className}`}>
      <CubMascot
        avatar={avatar}
        mood={speaking ? 'cheer' : 'think'}
        size={56}
        className={`tutor-cub ${speaking ? 'is-talking' : ''}`}
      />

      <div className="tutor-body">
        <strong className="tutor-who">Cub explains</strong>
        <p className="tutor-say">{say}</p>
      </div>

      <button
        type="button"
        className={`btn btn-sm btn-ghost tutor-toggle ${on ? 'is-on' : ''}`}
        onClick={toggle}
        aria-pressed={on}
        title={on ? 'Stop Cub reading this out' : 'Have Cub read this out'}
      >
        {on ? '🔊 Listening' : '🎧 Read it to me'}
      </button>
    </div>
  );
}
